import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Paperclip, X, FileText, Image as ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import AIDisclaimer from "../components/AIDisclaimer";
import api, { formatApiError } from "../lib/api";
import { AISourcePicker, defaultAISources } from "../components/AISourcePicker";
import { RECIPIENTS, createThread, runRound } from "../lib/boardRound";

const ALLOWED_EXT = ["pdf", "xlsx", "xls", "csv", "jpg", "jpeg", "png", "webp"];
const MAX_FILES = 3;
const MAX_BYTES = 20 * 1024 * 1024;
const COUNCIL = {
  id: "consiliu",
  name: "Consiliu comun",
  domain: "Toti cei 3 consultanti",
  focus: "Aceeasi intrebare, cate un raspuns din perspectiva fiecarui consultant.",
};

function formatAnswer(text) {
  return (text || "")
    .replace(/([;:,])?\s*\((\d{1,2})\)\s*/g, (_, p, n) => `${p === ":" ? ":" : ""}\n\n${n}. `)
    .replace(/([.!?])\s+(?=\d{1,2}\.\s)/g, "$1\n\n")
    .replace(/\s*(Puncte comune:|Diferente:)/g, "\n\n$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AIBoard() {
  const [consultants, setConsultants] = useState([]);
  const [active, setActive] = useState("buffett");
  const [question, setQuestion] = useState("");
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [sessions, setSessions] = useState({});
  const [attachments, setAttachments] = useState([]); // [{id, filename, content_type, size, is_image}]
  const [uploading, setUploading] = useState(false);
  const [src, setSrc] = useState(defaultAISources);
  const [threadId, setThreadId] = useState(null);
  const [title, setTitle] = useState("");
  const [recipient, setRecipient] = useState("buffett");
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/ai-board/consultants");
        setConsultants(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Nu pot incarca consultantii");
      }
    })();
  }, []);

  // Auto-grow textarea: 12-36 randuri.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22; // aprox px / rand
    const max = lineHeight * 36 + 16;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [question]);

  const uploadFile = async (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Tip nesuportat: .${ext}. Permise: ${ALLOWED_EXT.join(", ")}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} depaseste 20MB.`);
      return;
    }
    if (attachments.length >= MAX_FILES) {
      toast.error(`Maxim ${MAX_FILES} fisiere per intrebare.`);
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/api/ai-board/attachments", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload esuat");
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = "";
  };

  const onRemoveAttachment = async (att) => {
    setAttachments((a) => a.filter((x) => x.id !== att.id));
    try {
      await api.delete(`/api/ai-board/attachments/${att.id}`);
    } catch (_) {
      // ignore - server-side soft-delete failure nu blocheaza UI
    }
  };

  const onAsk = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q) return;
    setLoading(true);
    setConversation((c) => [
      ...c,
      { role: "user", text: q, ts: Date.now(), attachments: [...attachments] },
    ]);
    setQuestion("");
    const sendAttachmentIds = attachments.map((a) => a.id);
    setAttachments([]);
    let tid = threadId;
    if (!tid) {
      try {
        const t = await createThread({ consultantId: active, title: title.trim(), question: q });
        tid = t.id;
        setThreadId(t.id);
        setTitle(t.title);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Nu pot crea analiza");
        setLoading(false);
        return;
      }
    }
    const { msgs, newSessions } = await runRound({
      threadId: tid,
      recipient: threadId ? recipient : active,
      consultantIds: consultants.map((c) => c.id),
      question: q,
      attachmentIds: sendAttachmentIds,
      src,
      sessions,
      onSynthStart: () => setSynthesizing(true),
    });
    setSynthesizing(false);
    setSessions((s) => ({ ...s, ...newSessions }));
    setConversation((c) => [...c, ...msgs]);
    setSrc((s) => ({ ...s, sources: [] }));
    setLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      onAsk();
    }
  };

  const resetThread = () => {
    setSessions({});
    setConversation([]);
    setThreadId(null);
    setTitle("");
    setSrc(defaultAISources());
  };

  const onSwitchConsultant = (id) => {
    if (id === active) return;
    setActive(id);
    setRecipient(id);
    resetThread();
  };

  const allCards = consultants.length ? [...consultants, COUNCIL] : [];
  const activeMeta = allCards.find((c) => c.id === active);

  return (
    <Layout title="AI Board">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-slate-400">Consultanti</h3>
          {allCards.map((c) => (
            <button
              key={c.id}
              data-testid={`ai-consultant-${c.id}`}
              type="button"
              onClick={() => onSwitchConsultant(c.id)}
              className={`block w-full rounded-lg border p-3 text-left transition ${
                c.id === active ? "border-accent-500 bg-accent-500/10" : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-slate-500">{c.domain}</div>
              <div className="mt-1 text-[11px] text-slate-400">{c.focus}</div>
            </button>
          ))}
        </aside>

        <section className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col min-h-[60vh]">
          <div className="mb-3 border-b border-slate-200 pb-2">
            <div className="text-xs uppercase tracking-widest text-slate-400">Conversatie cu</div>
            <div data-testid="ai-active-name" className="text-base font-semibold">
              {activeMeta?.name || "..."}
            </div>
          </div>
          <AISourcePicker value={src} onChange={setSrc} disabled={loading} label={threadId ? "Surse suplimentare la raspuns" : "Surse date"} />
          {!threadId ? (
            <input
              data-testid="ai-thread-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Denumire analiza (optional, implicit primele 60 caractere din intrebare)"
              className="mb-3 w-full rounded border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none focus:border-accent-500"
            />
          ) : (
            <div data-testid="ai-thread-bar" className="mb-3 flex flex-wrap items-center gap-2 rounded border border-accent-500/30 bg-accent-500/5 px-3 py-2 text-xs text-slate-700">
              <span className="uppercase tracking-widest text-slate-400">Analiza</span>
              <span data-testid="ai-thread-title" className="font-semibold">{title}</span>
              <span className="ml-auto inline-flex items-center gap-2">
                Raspunde catre
                <select data-testid="ai-reply-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} disabled={loading} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs">
                  {RECIPIENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button data-testid="ai-new-thread-btn" type="button" onClick={resetThread} disabled={loading} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 hover:border-accent-500 disabled:opacity-50">
                  <Plus size={12} /> Analiza noua
                </button>
              </span>
            </div>
          )}
          {attachments.length > 0 && (
            <div
              data-testid="ai-attachment-chips"
              className="mb-3 flex flex-wrap gap-2"
            >
              {attachments.map((att) => (
                <span
                  key={att.id}
                  data-testid={`ai-attachment-chip-${att.id}`}
                  className="inline-flex items-center gap-2 rounded border border-accent-500/30 bg-accent-500/10 px-2 py-1 text-xs text-slate-700"
                >
                  {att.is_image ? <ImageIcon size={12} /> : <FileText size={12} />}
                  <span className="max-w-[160px] truncate">{att.filename}</span>
                  <span className="text-slate-400">{formatBytes(att.size)}</span>
                  <button
                    type="button"
                    data-testid={`ai-attachment-remove-${att.id}`}
                    onClick={() => onRemoveAttachment(att)}
                    className="text-slate-500 hover:text-slate-900"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <form
            onSubmit={onAsk}
            className="mb-3 flex gap-2 border-b border-slate-200 pb-3"
          >
            <input
              ref={fileInputRef}
              type="file"
              data-testid="ai-file-input"
              accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
              multiple
              hidden
              onChange={onPickFiles}
            />
            <button
              type="button"
              data-testid="ai-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading || attachments.length >= MAX_FILES}
              title="Ataseaza fisier (PDF/XLSX/XLS/CSV/JPG/PNG/WEBP, max 20MB)"
              className="self-start inline-flex items-center justify-center rounded border border-slate-200 bg-slate-100 px-2 py-2 text-slate-600 hover:border-accent-500 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Paperclip size={16} />}
            </button>
            <textarea
              ref={textareaRef}
              data-testid="ai-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={threadId ? "Raspunde la analiza: informatii suplimentare / corecturi... (Ctrl+Enter pentru trimitere)" : `Intreaba ${activeMeta?.name || ""}... (Ctrl+Enter pentru trimitere)`}
              rows={12}
              className="flex-1 resize-none rounded border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none focus:border-accent-500"
              style={{ minHeight: "312px", maxHeight: "840px" }}
              disabled={loading}
            />
            <button
              data-testid="ai-send-btn"
              type="submit"
              disabled={loading || !question.trim()}
              className="self-start inline-flex items-center gap-1.5 rounded bg-accent-500 px-3 py-2 text-sm font-semibold text-ink-950 disabled:opacity-50"
            >
              <Send size={14} /> {threadId ? "Raspunde" : "Trimite"}
            </button>
          </form>

          <div data-testid="ai-conversation" className="flex-1 space-y-3 overflow-y-auto pr-1">
            {conversation.length === 0 && (
              <p className="text-sm text-slate-400">
                Pune o intrebare. Poti atasa pana la {MAX_FILES} fisiere (PDF, XLSX, XLS, CSV, JPG, PNG, WEBP) de max 20MB fiecare.
              </p>
            )}
            {conversation.map((msg, i) => (
              <div
                key={i}
                data-testid={msg.synthesis ? "ai-msg-synthesis" : `ai-msg-${msg.role}`}
                className={`rounded p-3 text-sm ${
                  msg.role === "user"
                    ? "border border-slate-200 bg-slate-100"
                    : msg.role === "error"
                    ? "border border-red-500/40 bg-red-500/10 text-red-600"
                    : msg.synthesis
                    ? "border-2 border-accent-500 bg-accent-500/10"
                    : "border border-accent-500/30 bg-accent-500/5"
                }`}
              >
                {msg.role === "ai" && msg.consultant ? (
                  <div className="mb-1 text-xs uppercase tracking-widest text-accent-500">{msg.consultant}</div>
                ) : null}
                <div className="whitespace-pre-wrap leading-relaxed" data-testid="ai-board-message-text">{msg.role === "ai" ? formatAnswer(msg.text) : msg.text}</div>
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {msg.attachments.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-500"
                      >
                        {a.is_image ? <ImageIcon size={11} /> : <FileText size={11} />}
                        {a.filename}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="animate-spin" size={14} />{" "}
                {synthesizing
                  ? "Genereaza concluzia comuna..."
                  : (threadId ? recipient : active) === COUNCIL.id
                  ? "Consultantii genereaza raspunsurile..."
                  : "Genereaza raspuns..."}
              </div>
            )}
          </div>


          <AIDisclaimer />
        </section>

      </div>
    </Layout>
  );
}
